import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Save, FileText, Download, Plus, Trash2, RefreshCw, DollarSign, Loader2, AlertTriangle, Wand2, Eye, ChevronDown, ChevronRight, Upload, Package } from "lucide-react";


const SECTION_LABELS = {
  // Iter-136: flat federal offer binder layout matching the printed
  // TOC exactly.  Cover letter renders as page 2 (unnumbered).
  // Sections 1, 3, and Exhibit 11-A are form-slots (SF-1442, SF-30,
  // Wage Determination) — they are NOT in the section navigator
  // because the operator doesn't edit them here; they're spliced in
  // from PyMuPDF-filled form bytes at export time.
  cover_letter: "Cover Letter",
  pricing_schedule: "§ 2 · Schedule of Bid Items & Pricing",
  bid_guarantee: "§ 4 · Bid Guarantee",
  reps_and_certs: "§ 5 · Representations & Certifications",
  technical_proposal: "§ 6 · Technical Proposal",
  schedule_narrative: "§ 7 · Project Schedule",
  key_personnel: "§ 8 · Key Personnel & Org Chart",
  past_performance: "§ 9 · Relevant Experience & Past Performance",
  subcontracting: "§ 10 · Subcontracting & Compliance",
  davis_bacon_compliance: "§ 11 · Davis-Bacon Wage Compliance",
  buy_american: "§ 12 · Buy American Certification",
  insurance_bonding: "§ 13 · Insurance & Bonding",
  required_attachments: "§ 14 · Required Attachments & Appendices",
  statement_of_commitment: "§ 15 · Statement of Commitment",
  alternate_pricing: "Alternate Pricing (Optional)",
  // Legacy labels retained so proposals persisted before iter-135
  // still render their old sections in the sidebar with a "legacy"
  // treatment (the renderer folds them into the new spec at export).
  executive_summary: "Executive Summary (legacy)",
  management_plan: "Management Plan (legacy)",
  project_approach: "Project Approach (legacy)",
  staffing_plan: "Staffing Plan (legacy)",
  quality_control_plan: "Quality Control Plan (legacy)",
  safety_narrative: "Safety Narrative (legacy)",
  equipment_narrative: "Equipment Narrative (legacy)",
  risk_mitigation: "Risk Mitigation (legacy)",
  compliance_matrix: "Compliance Matrix (legacy)",
  proposal_checklist: "Proposal Checklist (legacy)",
};

// Canonical order the sidebar walks — new-spec sections first (all
// 14 numbered slots), then any legacy content that persisted in the
// proposal doc but doesn't have a new-spec slot.  The renderer's
// legacy-fallback resolver folds this content into the correct
// numbered section at export time.
const SECTION_ORDER = [
  "cover_letter",
  "alternate_pricing",
  "pricing_schedule",
  "bid_guarantee",
  "reps_and_certs",
  "technical_proposal",
  "schedule_narrative",
  "key_personnel",
  "past_performance",
  "subcontracting",
  "davis_bacon_compliance",
  "buy_american",
  "insurance_bonding",
  "required_attachments",
  "statement_of_commitment",
  // Legacy tail — only shown when the proposal already carries these.
  "executive_summary",
  "management_plan",
  "project_approach",
  "staffing_plan",
  "quality_control_plan",
  "safety_narrative",
  "equipment_narrative",
  "risk_mitigation",
  "compliance_matrix",
  "proposal_checklist",
];

const fmtMoney = (n) => (typeof n === "number" && !isNaN(n) ? `$${Math.round(n).toLocaleString()}` : "—");

export default function ProposalWorkspace() {
  const { noticeId } = useParams();
  const [proposal, setProposal] = useState(null);
  const [current, setCurrent] = useState("cover_letter");
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [format, setFormat] = useState(null);
  const [sov, setSov] = useState(null);
  const [sovSaving, setSovSaving] = useState(false);
  const [sovDirty, setSovDirty] = useState(false);
  const [regeneratingSection, setRegeneratingSection] = useState(null);   // slug currently being regenerated
  const [regeneratingAllStale, setRegeneratingAllStale] = useState(false);
  const [reconciling, setReconciling] = useState(false);

  // Iter-137: Section-Set Preview — fetched on mount so the operator
  // can see BEFORE clicking Generate which sections the RFP wording
  // actually asks for vs. which are federal-binder defaults. Every
  // section is still generated (Section N is a fixed printed TOC
  // slot), but the badging helps the operator decide where to focus
  // review time.
  const [sectionPreview, setSectionPreview] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Iter-138.2: Post-Award readiness — count workspace submittals by
  // the categories that get bundled into the Post-Award binder (safety
  // data sheets, product cut sheets, warranty docs, OSHA certs). The
  // strip under the download buttons shows the operator what's ready
  // without leaving the workspace.
  const [postAwardCounts, setPostAwardCounts] = useState(null);

  // Post-reconciliation callout — surfaced after saveSov when the backend
  // returns non-empty `reconciled` (bond/guarantee lines auto-corrected) or
  // `stale_sections` (narrative prose that still quotes the old total).
  // Persists until the operator dismisses or regenerates every stale section.
  const [reconcileReport, setReconcileReport] = useState(null);

  const staleSet = new Set(proposal?.stale_sections || []);
  const staleDetail = (proposal?.stale_reason?.detail || []);
  const staleForCurrent = staleDetail.filter((s) => s.section === current);

  const staleReasonPayload = proposal?.stale_reason;

  useEffect(() => {
    api.get(`/proposals/${noticeId}`).then(r => {
      setProposal(r.data);
      // Iter-136: auto-select the first section that actually has
      // content so a legacy proposal doesn't open on an empty
      // Cover Letter tab.
      const sections = r.data.sections || {};
      const firstFilled = SECTION_ORDER.find((k) => (sections[k] || "").trim().length > 0);
      const initial = firstFilled || "cover_letter";
      setCurrent(initial);
      setDraft(sections[initial] || "");
    }).catch(() => toast.error("No proposal found"));
    api.get(`/proposals/${noticeId}/export-format`).then(r => setFormat(r.data.format)).catch((err) => console.error("Export format lookup failed:", err));
    api.get(`/proposals/${noticeId}/sov`).then(r => {
      // Ensure every incoming row has a stable _rowId so React keys survive add/remove.
      const rows = (r.data.rows || []).map((row, i) => ({
        ...row,
        _rowId: row._rowId || `sov-${noticeId}-${i}-${row.item ?? i}`,
      }));
      setSov({ ...r.data, rows });
    }).catch((err) => console.error("Failed to load SOV:", err));

    // Live defects panel: fetch the pre-export defect list on mount so
    // operators see all blockers (bond promises, mobilization mismatches,
    // fabricated past performance, unnamed key personnel) without
    // clicking Reconcile first.  Silent on failure — the panel just
    // stays empty until the first Reconcile click.
    api.get(`/proposals/${noticeId}/defects`).then(r => {
      const defects = r.data?.export_defects || [];
      if (defects.length) {
        setReconcileReport(prev => prev || {
          reconciled: [],
          stale_sections: [],
          export_defects: defects,
          safe_to_export: r.data.safe_to_export !== false,
        });
      }
    }).catch(() => { /* silent — defects panel is a nice-to-have on mount */ });

    // Iter-137: Pull the section-set preview so the operator sees the
    // federal binder layout with REQUIRED_BY_RFP / DEFAULT_INCLUDED
    // badges. Silent on failure — the preview panel stays hidden.
    api.get(`/proposals/${noticeId}/section-preview`)
      .then(r => setSectionPreview(r.data))
      .catch(() => { /* silent */ });

    // Iter-138.2: Post-Award submittal counts. Silent on failure — the
    // strip just doesn't render if the request bombs.
    api.get(`/submittals`)
      .then(r => {
        const items = r.data?.items || [];
        const _bucket = (k) => items.filter(i => i.doc_type === k && i.include_in_binder !== false).length;
        setPostAwardCounts({
          sds: _bucket("sds"),
          product_cut_sheet: _bucket("product_cut_sheet"),
          warranty: _bucket("warranty"),
          osha_30: _bucket("osha_30") + _bucket("osha_10") + _bucket("cpr_first_aid"),
          total: items.filter(i => i.include_in_binder !== false && !["signature"].includes(i.doc_type)).length,
        });
      })
      .catch(() => { /* silent */ });
  }, [noticeId]);

  const openSection = (key) => { setCurrent(key); setDraft(proposal?.sections?.[key] || ""); };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/proposals/${noticeId}/section`, { section: current, content: draft });
      setProposal(p => ({ ...p, sections: { ...p.sections, [current]: draft } }));
      toast.success("Saved");
    } catch (err) {
      console.error("Section save failed:", err);
      toast.error("Save failed");
    }
    finally { setSaving(false); }
  };

  const sovTotal = (sov?.rows || []).reduce(
    (sum, r) => sum + (r.line_type === "alternate" ? 0 : (Number(r.amount) || 0)),
    0
  );
  const sovAlternatesTotal = (sov?.rows || []).reduce(
    (sum, r) => sum + (r.line_type === "alternate" ? (Number(r.amount) || 0) : 0),
    0
  );

  const updateSovRow = (idx, field, value) => {
    setSovDirty(true);
    setSov(prev => {
      const rows = prev.rows.map((r, i) => {
        if (i !== idx) return r;
        const next = { ...r };
        if (field === "description" || field === "unit" || field === "item" || field === "line_type") {
          next[field] = value;
        } else {
          next[field] = value === "" ? null : parseFloat(value);
        }
        // Auto-compute amount when qty and unit_price are both set (unless user typed an amount).
        if ((field === "qty" || field === "unit_price") && next.qty != null && next.unit_price != null) {
          next.amount = Math.round(next.qty * next.unit_price * 100) / 100;
        }
        return next;
      });
      return { ...prev, rows };
    });
  };

  const addSovRow = () => {
    setSovDirty(true);
    setSov(prev => ({
      ...prev,
      rows: [...(prev.rows || []), {
        _rowId: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `row-${Date.now()}-${Math.random()}`,
        item: String((prev.rows?.length || 0) + 1),
        description: "",
        qty: null,
        unit: null,
        unit_price: null,
        amount: null,
      }],
    }));
  };

  const removeSovRow = (idx) => {
    setSovDirty(true);
    setSov(prev => ({ ...prev, rows: prev.rows.filter((_, i) => i !== idx) }));
  };

  const saveSov = async () => {
    setSovSaving(true);
    try {
      const r = await api.put(`/proposals/${noticeId}/sov`, { rows: sov.rows });
      const rows = (r.data.rows || []).map((row, i) => ({
        ...row,
        _rowId: `sov-${noticeId}-saved-${i}-${row.item ?? i}`,
      }));
      setSov({ ...r.data, rows });
      setSovDirty(false);
      // Surface a callout when the backend auto-corrected bond amounts or
      // flagged narrative prose that quotes a superseded total. Operator sees
      // exactly what moved and gets a one-click regen per stale section.
      const recon = r.data.reconciled || [];
      const staleSlugs = r.data.stale_sections || [];
      if (recon.length || staleSlugs.length) {
        setReconcileReport({ reconciled: recon, stale_sections: staleSlugs, new_total: r.data.total });
      } else {
        setReconcileReport(null);
      }
      // Also pull fresh proposal doc so `stale_sections` + `stale_reason`
      // update (they live on the proposal, not the SOV response).
      const pr = await api.get(`/proposals/${noticeId}`);
      setProposal(pr.data);
      toast.success(recon.length
        ? `SOV saved — ${recon.length} bond line${recon.length === 1 ? "" : "s"} auto-reconciled`
        : "Schedule of Values saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSovSaving(false); }
  };

  // One-click "Reconcile all totals now" — re-runs saveSov against the
  // current rows without any edits, so the operator can force a resync
  // without touching an individual cell. This is the escape-hatch for
  // when they know the SOV was edited elsewhere (Estimator, MCP tool,
  // v1 API) and want the whole document brought back into agreement.
  const reconcileTotals = async () => {
    if (!sov) return;
    setReconciling(true);
    try {
      const r = await api.put(`/proposals/${noticeId}/sov`, { rows: sov.rows });
      const rows = (r.data.rows || []).map((row, i) => ({
        ...row,
        _rowId: `sov-${noticeId}-recon-${i}-${row.item ?? i}`,
      }));
      setSov({ ...r.data, rows });
      setSovDirty(false);
      const recon = r.data.reconciled || [];
      const staleSlugs = r.data.stale_sections || [];
      const exportDefects = r.data.export_defects || [];
      const safeToExport = r.data.safe_to_export === true;
      setReconcileReport(
        (recon.length || staleSlugs.length || exportDefects.length)
          ? { reconciled: recon, stale_sections: staleSlugs, export_defects: exportDefects, safe_to_export: safeToExport, new_total: r.data.total }
          : { reconciled: [], stale_sections: [], export_defects: [], safe_to_export: true, new_total: r.data.total, clean: true },
      );
      const pr = await api.get(`/proposals/${noticeId}`);
      setProposal(pr.data);
      toast.success(recon.length
        ? `${recon.length} bond line${recon.length === 1 ? "" : "s"} auto-reconciled against live base`
        : staleSlugs.length
          ? `Totals up to date — ${staleSlugs.length} section${staleSlugs.length === 1 ? "" : "s"} still quote the old number`
          : exportDefects.length
            ? `SOV in agreement, but ${exportDefects.length} export-gate defect${exportDefects.length === 1 ? "" : "s"} would still block export`
            : "Totals already in agreement across the whole proposal");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reconcile failed");
    } finally { setReconciling(false); }
  };

  // Per-section regen used by the stale-section banner. Costs 1 credit
  // (proposal_section) not 3 (full proposal), skips the monthly cap, and
  // preserves every other section verbatim.
  const regenerateSection = async (slug) => {
    setRegeneratingSection(slug);
    try {
      const r = await api.post(`/ai/proposal/${noticeId}/generate`, { sections_only: [slug] });
      setProposal(r.data);
      if (slug === current) setDraft(r.data.sections?.[slug] || "");
      // Trim the just-regenerated slug out of the callout list.
      setReconcileReport((prev) => prev ? {
        ...prev,
        stale_sections: (prev.stale_sections || []).filter((s) => s !== slug),
      } : prev);
      toast.success(`Regenerated ${SECTION_LABELS[slug] || slug} — 1 credit`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Regenerate failed");
    } finally { setRegeneratingSection(null); }
  };

  // Bulk regen for the "SOV moved late in the bid cycle" case — the operator
  // is watching a callout that lists 4-5 stale sections and doesn't want to
  // click each chip. Fires all stale slugs in a single API call; the backend
  // already parallelises the section generations with asyncio.gather so cost
  // in wall-clock time is roughly the same as regenerating one.
  const regenerateAllStale = async () => {
    const slugs = (reconcileReport?.stale_sections || []).filter(Boolean);
    if (slugs.length === 0) return;
    setRegeneratingAllStale(true);
    try {
      const r = await api.post(`/ai/proposal/${noticeId}/generate`, { sections_only: slugs });
      setProposal(r.data);
      if (current && slugs.includes(current)) {
        setDraft(r.data.sections?.[current] || "");
      }
      setReconcileReport((prev) => prev ? { ...prev, stale_sections: [] } : prev);
      toast.success(`Regenerated ${slugs.length} section${slugs.length === 1 ? "" : "s"} — ${slugs.length} credit${slugs.length === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk regenerate failed");
    } finally { setRegeneratingAllStale(false); }
  };

  const resetSov = async () => {
    if (!window.confirm("Drop your edits and re-derive from the Estimating Agent?")) return;
    setSovSaving(true);
    try {
      const r = await api.delete(`/proposals/${noticeId}/sov`);
      const rows = (r.data.rows || []).map((row, i) => ({
        ...row,
        _rowId: `sov-${noticeId}-reset-${i}-${row.item ?? i}`,
      }));
      setSov({ ...r.data, rows });
      setSovDirty(false);
      toast.success("Reset to auto-populated draft");
    } catch (err) {
      console.error("SOV reset failed:", err);
      toast.error("Reset failed");
    }
    finally { setSovSaving(false); }
  };

  if (!proposal) return <div className="p-8 text-slate-400">Loading…</div>;

  const downloadPdf = (fmt) => {
    const token = localStorage.getItem("bf_token");
    const params = new URLSearchParams();
    if (token) params.set("auth", token);
    if (fmt) params.set("format", fmt);
    const qs = params.toString();
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/proposals/${noticeId}/download.pdf${qs ? `?${qs}` : ""}`;
    window.open(url, "_blank");
  };

  return (
    <div className="p-8 stagger-in" data-testid="proposal-workspace">
      <Link to={`/opportunities/${noticeId}`} className="mono-label text-slate-400 hover:text-cyan-400 flex items-center gap-2 mb-6" data-testid="back-to-opp"><ArrowLeft className="w-3.5 h-3.5" />BACK</Link>
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="mono-label text-cyan-400 mb-2">// PROPOSAL WORKSPACE</div>
          <h1 className="font-display font-black text-3xl tracking-tight">{proposal.title}</h1>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => downloadPdf(null)}
              className="bg-cyan-400 text-[#070B14] px-5 py-2.5 mono-label hover:bg-cyan-300 flex items-center gap-2"
              data-testid="download-submittal"
              title="Flat 15-section federal offer binder — SF-1442, pricing, technical proposal, reps & certs, wage compliance, insurance & bonding, statement of commitment."
            >
              <Download className="w-4 h-4" />DOWNLOAD SUBMITTAL
            </button>
            <button
              onClick={() => downloadPdf("binder")}
              className="border border-cyan-400 text-cyan-400 hover:bg-cyan-400/10 px-5 py-2.5 mono-label flex items-center gap-2"
              data-testid="download-post-award"
              title="Post-award submittals binder — safety data sheets, material submittals, warranty documentation. Assembled from workspace submittals uploaded under Company Profile."
            >
              <Download className="w-4 h-4" />DOWNLOAD POST-AWARD
            </button>
          </div>
          {postAwardCounts && (
            <Link
              to="/submittals"
              className="text-[11px] text-slate-400 hover:text-cyan-300 flex items-center gap-2 font-mono"
              data-testid="post-award-readiness-strip"
              title="Manage the submittal library that powers Download Post-Award"
            >
              <Package className="w-3 h-3" />
              Post-Award library:{" "}
              <span className="text-emerald-300">{postAwardCounts.sds}</span> SDS ·{" "}
              <span className="text-emerald-300">{postAwardCounts.product_cut_sheet}</span> cut sheets ·{" "}
              <span className="text-emerald-300">{postAwardCounts.warranty}</span> warranty ·{" "}
              <span className="text-emerald-300">{postAwardCounts.osha_30}</span> safety certs
              <Upload className="w-3 h-3 ml-1" />
              <span className="underline decoration-dotted underline-offset-2">upload / manage</span>
            </Link>
          )}
        </div>
      </div>

      {Array.isArray(proposal.variant_pricing) && proposal.variant_pricing.length >= 2 && (
        <div className="mb-6 bg-fuchsia-400/5 border border-fuchsia-400/30 p-4" data-testid="proposal-variant-pricing">
          <div className="mono-label text-fuchsia-300 mb-3">// PRICED OPTIONS — THIS PROPOSAL COVERS ALL</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {proposal.variant_pricing.map((v, i) => (
              <div key={i} className={`border p-3 ${v.is_primary ? "border-fuchsia-400 bg-fuchsia-400/10" : "border-white/10 bg-white/[0.02]"}`} data-testid={`proposal-variant-${i}`}>
                <div className="flex items-center gap-2 text-sm text-slate-200 font-medium">
                  {v.is_primary && <span className="text-amber-300">★</span>}
                  <span className="truncate">{v.name}</span>
                  {v.is_primary && <span className="mono-label text-[9px] text-amber-300 ml-auto">PRIMARY</span>}
                </div>
                <div className="mt-1 text-xl font-black tabular-nums text-white">
                  {fmtMoney(v.grand_total)}
                </div>
                {v.alt_scope_prompt && (
                  <div className="mt-1 text-[11px] text-slate-400 line-clamp-2">{v.alt_scope_prompt}</div>
                )}
              </div>
            ))}
          </div>
          <div className="mt-3 text-[11px] text-slate-400">
            The Alternate Pricing section (in the sidebar) contains the full comparison table.
          </div>
        </div>
      )}

      {sectionPreview && (
        <div className="mb-6 bg-[#0D1424] border border-white/10" data-testid="section-preview-panel">
          <button
            onClick={() => setPreviewOpen(o => !o)}
            className="w-full p-4 border-b border-white/10 flex items-center justify-between hover:bg-white/[0.02]"
            data-testid="section-preview-toggle"
          >
            <div className="flex items-center gap-3">
              <Eye className="w-4 h-4 text-cyan-400" />
              <div className="text-left">
                <div className="mono-label text-cyan-400">// SECTION-SET PREVIEW · FEDERAL BINDER LAYOUT</div>
                <div className="text-xs text-slate-400 mt-0.5">
                  {(() => {
                    const req = sectionPreview.sections.filter(s => s.status === "REQUIRED_BY_RFP").length;
                    const def = sectionPreview.sections.filter(s => s.status === "DEFAULT_INCLUDED").length;
                    const mand = sectionPreview.sections.filter(s => s.status === "MANDATORY").length;
                    const forms = (sectionPreview.form_slots || []).length;
                    return (
                      <>
                        <span className="text-emerald-300 font-bold">{req}</span> required by RFP ·{" "}
                        <span className="text-slate-300 font-bold">{def}</span> federal default ·{" "}
                        <span className="text-slate-300 font-bold">{mand}</span> cover ·{" "}
                        <span className="text-fuchsia-300 font-bold">{forms}</span> form slots
                        {!sectionPreview.has_ito_text && (
                          <span className="ml-2 text-amber-300">· RFP text short — everything defaulted in</span>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
            {previewOpen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
          </button>
          {previewOpen && (
            <div className="p-4 space-y-4" data-testid="section-preview-body">
              {!sectionPreview.has_ito_text && (
                <div className="border border-amber-400/40 bg-amber-400/5 px-3 py-2 text-xs text-amber-200" data-testid="section-preview-short-rfp">
                  <AlertTriangle className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5" />
                  The stored RFP text is under 200 characters, so the trigger scan can't classify which sections the solicitation actually asks for. All sections are marked DEFAULT_INCLUDED — upload the full ITO / Section L to sharpen this preview.
                </div>
              )}
              {sectionPreview.form_slots?.length > 0 && (
                <div>
                  <div className="mono-label text-fuchsia-300 text-[10px] mb-2">FORM SLOTS · SPLICED AT EXPORT (not LLM-generated)</div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {sectionPreview.form_slots.map((f) => (
                      <div key={f.key} className="border border-fuchsia-400/30 bg-fuchsia-400/5 px-3 py-2" data-testid={`section-preview-form-${f.key}`}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-sm font-bold text-fuchsia-200">§ {f.section_number} · {f.label}</div>
                          <span className="text-[9px] font-mono font-bold tracking-widest text-fuchsia-300 bg-fuchsia-400/10 border border-fuchsia-400/30 px-1.5 py-0.5">{f.kind.toUpperCase()}</span>
                        </div>
                        <div className="text-[11px] text-slate-400 mt-1 leading-relaxed">{f.note}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <div className="mono-label text-cyan-300 text-[10px] mb-2">NARRATIVE SECTIONS · GENERATED BY THE PROPOSAL AI</div>
                <div className="space-y-1">
                  {sectionPreview.sections.map((s) => {
                    const badgeCls =
                      s.status === "REQUIRED_BY_RFP"
                        ? "text-emerald-300 bg-emerald-400/10 border-emerald-400/40"
                        : s.status === "MANDATORY"
                          ? "text-sky-300 bg-sky-400/10 border-sky-400/40"
                          : "text-slate-400 bg-white/5 border-white/15";
                    const badgeText =
                      s.status === "REQUIRED_BY_RFP" ? "REQUIRED BY RFP"
                        : s.status === "MANDATORY" ? "COVER · MANDATORY"
                        : "FEDERAL DEFAULT";
                    return (
                      <div
                        key={s.key}
                        className="flex items-start gap-3 py-2 px-2 border border-white/5 hover:border-white/10 hover:bg-white/[0.02]"
                        data-testid={`section-preview-row-${s.key}`}
                      >
                        <div className="w-8 shrink-0 text-right font-mono text-xs text-slate-500 pt-0.5">
                          {s.section_number != null ? `§ ${s.section_number}` : "—"}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm text-slate-200">{s.label}</span>
                            <span className={`text-[9px] font-mono font-bold tracking-widest border px-1.5 py-0.5 ${badgeCls}`} data-testid={`section-preview-${s.key}-badge`}>
                              {badgeText}
                            </span>
                          </div>
                          {s.matched_triggers?.length > 0 && (
                            <div className="mt-1 flex flex-wrap gap-1" data-testid={`section-preview-${s.key}-triggers`}>
                              {s.matched_triggers.slice(0, 5).map((t, i) => (
                                <span key={i} className="text-[10px] font-mono text-emerald-200 bg-emerald-400/5 border border-emerald-400/30 px-1.5 py-0.5">
                                  {t}
                                </span>
                              ))}
                              {s.matched_triggers.length > 5 && (
                                <span className="text-[10px] font-mono text-slate-500">+ {s.matched_triggers.length - 5} more</span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="text-[11px] text-slate-500 leading-relaxed border-t border-white/5 pt-3">
                Every section listed above is a fixed slot in the federal offer binder — the Section N number is printed regardless of the RFP wording. Sections tagged <span className="text-emerald-300 font-mono">REQUIRED BY RFP</span> are the ones where the solicitation text explicitly asked for the content (keywords shown). Sections tagged <span className="text-slate-300 font-mono">FEDERAL DEFAULT</span> are included by binder convention; review those first if you need to trim generation credits.
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-1 bg-[#0D1424] border border-white/10">
          <div className="p-4 border-b border-white/10 mono-label text-cyan-400">SECTIONS</div>
          <div className="p-2">
            {SECTION_ORDER
              .filter((key) => {
                // Only show a legacy section in the sidebar if this
                // proposal actually carries content for it (so the
                // panel stays clean for new proposals but still lets
                // operators edit content from older iterations).
                const isLegacy = /\(legacy\)/.test(SECTION_LABELS[key] || "");
                const hasContent = !!proposal.sections?.[key];
                return !isLegacy || hasContent;
              })
              .map((key) => {
              const label = SECTION_LABELS[key] || key;
              const has = !!proposal.sections?.[key];
              const active = current === key;
              const isStale = staleSet.has(key);
              const previewRow = sectionPreview?.sections?.find((s) => s.key === key);
              const previewStatus = previewRow?.status;
              return (
                <button key={key} onClick={() => openSection(key)} className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between transition-colors ${active ? "bg-cyan-400/10 text-cyan-300 border-l-2 border-cyan-400" : "text-slate-300 hover:bg-white/5 border-l-2 border-transparent"}`} data-testid={`section-${key}`}>
                  <span className="truncate flex items-center gap-1.5">
                    {label}
                    {isStale && <AlertTriangle className="w-3 h-3 text-amber-400 shrink-0" data-testid={`section-${key}-stale-badge`} />}
                    {previewStatus === "REQUIRED_BY_RFP" && (
                      <span className="shrink-0 inline-block w-1.5 h-1.5 rounded-full bg-emerald-400" title="Explicitly required by RFP text" data-testid={`section-${key}-required-dot`} />
                    )}
                    {previewStatus === "DEFAULT_INCLUDED" && (
                      <span className="shrink-0 inline-block w-1.5 h-1.5 rounded-full bg-slate-500" title="Included by federal binder default (RFP silent)" data-testid={`section-${key}-default-dot`} />
                    )}
                  </span>
                  {has && <FileText className="w-3 h-3 text-cyan-400 shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>

        <div className="lg:col-span-3 bg-[#0D1424] border border-white/10">
          <div className="p-4 border-b border-white/10 flex items-center justify-between">
            <div className="font-display font-bold">{SECTION_LABELS[current]}</div>
            <button onClick={save} disabled={saving} className="bg-cyan-400 text-[#070B14] px-4 py-2 mono-label hover:bg-cyan-300 flex items-center gap-2 disabled:opacity-50" data-testid="save-section">
              <Save className="w-3.5 h-3.5" />{saving ? "Saving" : "Save"}
            </button>
          </div>
          {staleSet.has(current) && (
            <div
              className="border-b border-amber-400/40 bg-amber-400/10 px-6 py-3 flex items-start gap-3"
              data-testid={`stale-banner-${current}`}
            >
              <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="mono-label text-amber-300 text-[10px]">TOTALS CHANGED — THIS SECTION IS OUT OF DATE</div>
                <div className="text-sm text-amber-100 mt-1 leading-relaxed">
                  This section quotes a total that no longer matches the Schedule of Values. Regenerate to bring the prose back into agreement without the operator having to guess which sentence still points at the old number.
                </div>
                {staleForCurrent.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {staleForCurrent.slice(0, 3).map((s, i) => (
                      <div key={i} className="text-[11px] font-mono text-amber-200/80">
                        quoted <span className="text-amber-100 font-bold">${s.quoted_total?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        {" · SOV now "}
                        <span className="text-amber-100 font-bold">${s.sov_total?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        {" ("}
                        <span className={(s.delta || 0) < 0 ? "text-emerald-300" : "text-rose-300"}>
                          {(s.delta || 0) >= 0 ? "+" : ""}${Math.abs(s.delta || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                        {")"}
                      </div>
                    ))}
                    {staleForCurrent.length > 3 && (
                      <div className="text-[10px] text-amber-200/60">
                        + {staleForCurrent.length - 3} more mention{staleForCurrent.length - 3 === 1 ? "" : "s"}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <button
                onClick={() => regenerateSection(current)}
                disabled={regeneratingSection === current}
                className="border border-amber-400 text-amber-300 hover:bg-amber-400/20 px-3 py-2 mono-label text-xs flex items-center gap-1.5 disabled:opacity-50 whitespace-nowrap self-center"
                data-testid={`stale-regenerate-${current}`}
              >
                {regeneratingSection === current ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                {regeneratingSection === current ? "REGENERATING" : "REGENERATE · 1 CREDIT"}
              </button>
            </div>
          )}
          <textarea value={draft} onChange={(e) => setDraft(e.target.value)} className="w-full min-h-[600px] bg-transparent border-0 p-6 text-slate-200 leading-relaxed outline-none resize-none font-mono text-sm" data-testid="section-editor" />
        </div>
      </div>

      {/* ============ SCHEDULE OF VALUES EDITOR ============ */}
      {sov && (
        <div className="mt-8 bg-[#0D1424] border border-white/10" data-testid="sov-editor">
          <div className="p-4 border-b border-white/10 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <DollarSign className="w-5 h-5 text-emerald-400" />
              <div>
                <div className="mono-label text-emerald-400">// SCHEDULE OF VALUES</div>
                <div className="font-display font-bold text-lg">Pricing table for every export</div>
              </div>
              <div className="ml-3 hidden md:flex items-center gap-2">
                {sov.source === "override" && <span className="text-[10px] font-mono font-bold tracking-widest text-emerald-300 bg-emerald-400/10 border border-emerald-400/30 rounded-full px-2 py-0.5" data-testid="sov-source-badge">EDITED</span>}
                {sov.source === "estimate" && <span className="text-[10px] font-mono font-bold tracking-widest text-sky-300 bg-sky-400/10 border border-sky-400/30 rounded-full px-2 py-0.5" data-testid="sov-source-badge">FROM ESTIMATE</span>}
                {sov.source === "auto_split" && <span className="text-[10px] font-mono font-bold tracking-widest text-amber-300 bg-amber-400/10 border border-amber-400/30 rounded-full px-2 py-0.5" data-testid="sov-source-badge">AUTO SPLIT</span>}
                {sov.source === "blank" && <span className="text-[10px] font-mono font-bold tracking-widest text-slate-300 bg-white/5 border border-white/10 rounded-full px-2 py-0.5" data-testid="sov-source-badge">FILL IN</span>}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={addSovRow} className="border border-white/20 hover:border-cyan-400 hover:text-cyan-400 px-3 py-2 mono-label flex items-center gap-1.5 text-xs" data-testid="sov-add-row">
                <Plus className="w-3.5 h-3.5" />ADD ROW
              </button>
              <button
                onClick={reconcileTotals}
                disabled={reconciling || sovSaving}
                className="border border-emerald-400/60 hover:border-emerald-400 hover:bg-emerald-400/10 text-emerald-300 px-3 py-2 mono-label flex items-center gap-1.5 text-xs disabled:opacity-50"
                data-testid="sov-reconcile"
                title="Recompute every bond/guarantee percentage against the current SOV base and re-flag any narrative section that still quotes an old total. Use after a bulk edit or when the numbers came in via the Estimator / MCP tool / v1 API."
              >
                {reconciling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                {reconciling ? "RECONCILING" : "RECONCILE TOTALS"}
              </button>
              <button onClick={resetSov} disabled={sovSaving} className="border border-white/20 hover:border-amber-400 hover:text-amber-400 px-3 py-2 mono-label flex items-center gap-1.5 text-xs disabled:opacity-50" data-testid="sov-reset">
                <RefreshCw className="w-3.5 h-3.5" />RESET
              </button>
              <button onClick={saveSov} disabled={sovSaving || !sovDirty} className="bg-emerald-400 text-[#070B14] px-4 py-2 mono-label hover:bg-emerald-300 flex items-center gap-1.5 text-xs disabled:opacity-50" data-testid="sov-save">
                {sovSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}{sovSaving ? "SAVING" : "SAVE SOV"}
              </button>
            </div>
          </div>
          {reconcileReport && (
            <div
              className="border-b border-emerald-400/30 bg-emerald-400/5 px-4 py-3"
              data-testid="sov-reconcile-report"
            >
              <div className="flex items-start gap-3">
                <Wand2 className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  {reconcileReport.clean ? (
                    <>
                      <div className="mono-label text-emerald-300 text-[10px]">TOTALS RECONCILED · ALL IN AGREEMENT</div>
                      <div className="text-xs text-slate-300 mt-1">Every bond line matches its stated rate; every narrative section quotes the current SOV total; every export gate would pass. Safe to export.</div>
                    </>
                  ) : (
                    <>
                      <div className="mono-label text-emerald-300 text-[10px]">
                        TOTALS RECONCILED
                        {reconcileReport.reconciled.length > 0 && ` · ${reconcileReport.reconciled.length} BOND LINE${reconcileReport.reconciled.length === 1 ? "" : "S"} AUTO-CORRECTED`}
                        {reconcileReport.stale_sections.length > 0 && ` · ${reconcileReport.stale_sections.length} STALE SECTION${reconcileReport.stale_sections.length === 1 ? "" : "S"}`}
                        {(reconcileReport.export_defects?.length || 0) > 0 && ` · ${reconcileReport.export_defects.length} EXPORT GATE${reconcileReport.export_defects.length === 1 ? "" : "S"} WOULD BLOCK`}
                      </div>
                      {(reconcileReport.export_defects?.length || 0) > 0 && (
                        <div className="mt-2 pt-2 border-t border-rose-400/20">
                          <div className="text-xs text-rose-200 mb-1.5 flex items-center gap-1.5">
                            <AlertTriangle className="w-3 h-3" />
                            SOV is in agreement, but the export will still be refused for these defects:
                          </div>
                          <div className="space-y-0.5 max-h-32 overflow-y-auto">
                            {reconcileReport.export_defects.map((d, i) => (
                              <div key={i} className="text-[11px] font-mono text-rose-100/90" data-testid={`sov-reconcile-defect-${i}`}>
                                {d.gate === "key_personnel_unnamed_in_narrative" && (
                                  <>
                                    [{d.section}] <span className="text-slate-400">{d.role}</span> · <span className="text-rose-200">"{d.matched_phrase}"</span>
                                    {d.roster_name && <span className="text-slate-400"> → roster: {d.roster_name}</span>}
                                  </>
                                )}
                                {d.gate === "bond_promise_pricing_mismatch" && (
                                  <>
                                    [{d.section}] <span className="text-slate-400">promises {d.bond}</span> · <span className="text-rose-200">"{d.matched_phrase}"</span> <span className="text-slate-400">but no priced line</span>
                                  </>
                                )}
                                {d.gate === "mobilization_consistency_mismatch" && (
                                  <>
                                    [{d.section}] <span className="text-rose-200">"{d.phrase}"</span> <span className="text-slate-400">≈ {d.hours}h</span>
                                    <span className="text-slate-400"> vs {(d.primary_sections || []).join(", ") || "other sections"} at </span>
                                    <span className="text-amber-200">{d.primary_hours}h</span>
                                  </>
                                )}
                                {d.gate === "past_performance_fabrication" && (
                                  <>
                                    [{d.section}] <span className="text-rose-200">"{d.matched_phrase}"</span>
                                    <span className="text-slate-400"> — {d.reason || "not in workspace PP library"}</span>
                                  </>
                                )}
                                {d.gate === "unpriced_rows" && (
                                  <>
                                    [row {d.item}] <span className="text-rose-200">"{(d.description || "").slice(0, 60)}"</span>
                                    <span className="text-slate-400"> · unit_price ${(d.unit_price ?? 0).toFixed(2)} · qty {d.qty ?? "—"} · </span>
                                    <span className="text-amber-200">amount blank</span>
                                  </>
                                )}
                                {d.gate === "sov_out_of_sync" && (
                                  <>
                                    <span className="text-rose-200">SOV out of sync</span>
                                    <span className="text-slate-400"> — {d.message || d.code}</span>
                                  </>
                                )}
                                {d.gate === "cover_letter_addressee_incomplete" && (
                                  <>
                                    <span className="text-rose-200">Cover-letter addressee incomplete</span>
                                    <span className="text-slate-400"> — SAM.gov didn't ship a CO name; the ``Attn:`` line will render blank</span>
                                  </>
                                )}
                                {!["key_personnel_unnamed_in_narrative", "bond_promise_pricing_mismatch", "mobilization_consistency_mismatch", "past_performance_fabrication", "unpriced_rows", "sov_out_of_sync", "cover_letter_addressee_incomplete"].includes(d.gate) && (
                                  <>
                                    [{d.gate}] {d.section && <span className="text-slate-400">{d.section} · </span>}
                                    <span className="text-rose-200">{d.matched_phrase || d.message || JSON.stringify(d).slice(0, 100)}</span>
                                  </>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {reconcileReport.reconciled.length > 0 && (
                        <div className="mt-2 space-y-0.5 max-h-32 overflow-y-auto">
                          {reconcileReport.reconciled.map((m, i) => (
                            <div key={i} className="text-[11px] font-mono text-emerald-100/80" data-testid={`sov-reconcile-line-${i}`}>
                              {m.item}. {m.description} · <span className="text-slate-400">was</span> ${m.stored_amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              {" → "}
                              <span className="text-emerald-200 font-bold">${m.expected_amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                              {" "}<span className={(m.delta || 0) >= 0 ? "text-emerald-300" : "text-rose-300"}>({(m.delta || 0) >= 0 ? "+" : ""}${Math.abs(m.delta || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {reconcileReport.stale_sections.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-emerald-400/20">
                          <div className="text-xs text-amber-200 mb-2 flex items-center justify-between gap-3 flex-wrap">
                            <span>
                              <AlertTriangle className="inline w-3 h-3 mr-1" />
                              {reconcileReport.stale_sections.length} section{reconcileReport.stale_sections.length === 1 ? "" : "s"} still quote{reconcileReport.stale_sections.length === 1 ? "s" : ""} a superseded total.
                            </span>
                            {reconcileReport.stale_sections.length > 1 && (
                              <button
                                onClick={regenerateAllStale}
                                disabled={regeneratingAllStale || !!regeneratingSection}
                                className="bg-amber-400 text-[#070B14] hover:bg-amber-300 px-3 py-1.5 mono-label text-[10px] flex items-center gap-1.5 disabled:opacity-50"
                                data-testid="sov-regenerate-all-stale"
                              >
                                {regeneratingAllStale ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
                                REGENERATE ALL {reconcileReport.stale_sections.length} · {reconcileReport.stale_sections.length} CREDITS
                              </button>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {reconcileReport.stale_sections.map((slug) => (
                              <button
                                key={slug}
                                onClick={() => regenerateSection(slug)}
                                disabled={regeneratingSection === slug || regeneratingAllStale}
                                className="border border-amber-400/50 hover:border-amber-400 hover:bg-amber-400/10 text-amber-200 px-2.5 py-1 mono-label text-[10px] flex items-center gap-1.5 disabled:opacity-50"
                                data-testid={`sov-reconcile-regen-${slug}`}
                              >
                                {regeneratingSection === slug || regeneratingAllStale ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
                                {(SECTION_LABELS[slug] || slug).toUpperCase()}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
                <button
                  onClick={() => setReconcileReport(null)}
                  className="text-slate-500 hover:text-slate-300 text-xs mono-label"
                  data-testid="sov-reconcile-dismiss"
                >
                  DISMISS
                </button>
              </div>
            </div>
          )}
          <div className="p-4 text-xs text-slate-400 border-b border-white/10">
            Edit any cell below to override the auto-populated draft. Extended amount auto-computes from <span className="text-emerald-300">Qty × Unit Price</span> unless you type an explicit amount. Your saved SOV appears in the Binder PDF, narrative PDF, and DOCX exports.
            <div className="mt-2 flex flex-wrap gap-4 text-[11px]">
              <span className="flex items-center gap-1.5">
                <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-emerald-500/15 border border-emerald-400/40 text-emerald-300">
                  <svg viewBox="0 0 20 20" fill="currentColor" className="w-2.5 h-2.5"><path fillRule="evenodd" d="M16.7 5.3a1 1 0 0 1 0 1.4l-7 7a1 1 0 0 1-1.4 0l-3.5-3.5a1 1 0 1 1 1.4-1.4L9 11.6l6.3-6.3a1 1 0 0 1 1.4 0z" clipRule="evenodd"/></svg>
                </span>
                <span className="text-slate-400">= Qty anchored to measured takeoff</span>
              </span>
              <span className="text-slate-400">
                <span className="text-amber-300 font-mono font-bold">ALTERNATE</span> = priced substitute for a base line; excluded from grand total
              </span>
            </div>
          </div>
          <div className="overflow-x-auto p-4">
            <table className="w-full text-sm" data-testid="sov-table">
              <thead>
                <tr className="border-b border-white/10 text-left text-[10px] font-mono font-bold tracking-widest text-slate-400">
                  <th className="pb-2 pr-2 w-12">ITEM</th>
                  <th className="pb-2 pr-2 w-24">TYPE</th>
                  <th className="pb-2 pr-2">DESCRIPTION</th>
                  <th className="pb-2 pr-2 w-20">QTY</th>
                  <th className="pb-2 pr-2 w-8" title="Takeoff-anchored"></th>
                  <th className="pb-2 pr-2 w-20">UNIT</th>
                  <th className="pb-2 pr-2 w-28">UNIT PRICE</th>
                  <th className="pb-2 pr-2 w-28">AMOUNT</th>
                  <th className="pb-2 pr-2 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {(sov.rows || []).map((r, i) => (
                  <tr key={r._rowId || `row-${i}`} className={`border-b border-white/5 hover:bg-white/[0.02] ${(r.line_type === "alternate") ? "bg-amber-500/5" : ""}`} data-testid={`sov-row-${i}`}>
                    <td className="py-1.5 pr-2">
                      <input value={r.item ?? ""} onChange={(e) => updateSovRow(i, "item", e.target.value)} className="w-10 bg-transparent border border-white/10 focus:border-cyan-400 outline-none px-2 py-1 text-slate-200" data-testid={`sov-row-${i}-item`} />
                    </td>
                    <td className="py-1.5 pr-2">
                      <select
                        value={r.line_type || "base"}
                        onChange={(e) => updateSovRow(i, "line_type", e.target.value)}
                        className={`w-full bg-transparent border border-white/10 focus:border-cyan-400 outline-none px-2 py-1 text-[11px] font-mono font-bold tracking-widest ${
                          r.line_type === "alternate" ? "text-amber-300"
                          : r.line_type === "option" ? "text-sky-300"
                          : "text-slate-300"
                        }`}
                        data-testid={`sov-row-${i}-line-type`}
                      >
                        <option value="base" className="bg-[#0D1424]">BASE</option>
                        <option value="option" className="bg-[#0D1424]">OPTION</option>
                        <option value="alternate" className="bg-[#0D1424]">ALTERNATE</option>
                      </select>
                    </td>
                    <td className="py-1.5 pr-2">
                      <input value={r.description ?? ""} onChange={(e) => updateSovRow(i, "description", e.target.value)} placeholder="e.g. Middle Residence Roofing" className="w-full bg-transparent border border-white/10 focus:border-cyan-400 outline-none px-2 py-1 text-slate-200" data-testid={`sov-row-${i}-desc`} />
                    </td>
                    <td className="py-1.5 pr-2">
                      <input type="number" step="any" value={r.qty ?? ""} onChange={(e) => updateSovRow(i, "qty", e.target.value)} className="w-full bg-transparent border border-white/10 focus:border-cyan-400 outline-none px-2 py-1 text-slate-200 text-right" data-testid={`sov-row-${i}-qty`} />
                    </td>
                    <td className="py-1.5 pr-2 text-center align-middle" data-testid={`sov-row-${i}-anchor`}>
                      {r.takeoff_anchored ? (
                        <span
                          title={r.assumption || "Quantity anchored to measured takeoff"}
                          className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500/15 border border-emerald-400/40 text-emerald-300"
                          data-testid={`sov-row-${i}-anchor-badge`}
                        >
                          <svg viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3"><path fillRule="evenodd" d="M16.7 5.3a1 1 0 0 1 0 1.4l-7 7a1 1 0 0 1-1.4 0l-3.5-3.5a1 1 0 1 1 1.4-1.4L9 11.6l6.3-6.3a1 1 0 0 1 1.4 0z" clipRule="evenodd"/></svg>
                        </span>
                      ) : (
                        <span className="text-slate-600 text-[9px] font-mono">—</span>
                      )}
                    </td>
                    <td className="py-1.5 pr-2">
                      <input value={r.unit ?? ""} onChange={(e) => updateSovRow(i, "unit", e.target.value)} placeholder="LS" className="w-full bg-transparent border border-white/10 focus:border-cyan-400 outline-none px-2 py-1 text-slate-200" data-testid={`sov-row-${i}-unit`} />
                    </td>
                    <td className="py-1.5 pr-2">
                      <input type="number" step="any" value={r.unit_price ?? ""} onChange={(e) => updateSovRow(i, "unit_price", e.target.value)} className="w-full bg-transparent border border-white/10 focus:border-cyan-400 outline-none px-2 py-1 text-slate-200 text-right" data-testid={`sov-row-${i}-unit-price`} />
                    </td>
                    <td className="py-1.5 pr-2">
                      <input type="number" step="any" value={r.amount ?? ""} onChange={(e) => updateSovRow(i, "amount", e.target.value)} className="w-full bg-transparent border border-white/10 focus:border-cyan-400 outline-none px-2 py-1 text-emerald-300 font-mono font-bold text-right" data-testid={`sov-row-${i}-amount`} />
                    </td>
                    <td className="py-1.5 pr-2 text-center">
                      <button onClick={() => removeSovRow(i)} className="text-slate-500 hover:text-rose-400 transition-colors" data-testid={`sov-row-${i}-remove`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
                <tr className="border-t-2 border-emerald-400/40 bg-emerald-400/5">
                  <td className="py-3 pr-2"></td>
                  <td className="py-3 pr-2"></td>
                  <td className="py-3 pr-2 font-display font-bold uppercase text-emerald-300 tracking-widest text-xs">
                    {(sov.rows || []).some(r => r.line_type === "alternate")
                      ? "Total Base + Options (alternates excluded)"
                      : "Total Contract Value"}
                  </td>
                  <td colSpan={4}></td>
                  <td className="py-3 pr-2 text-right font-display font-black text-emerald-300 text-lg" data-testid="sov-total">
                    ${sovTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td></td>
                </tr>
                {(sov.rows || []).some(r => r.line_type === "alternate") && (
                  <tr className="border-b border-amber-400/30 bg-amber-400/5" data-testid="sov-alternates-subtotal-row">
                    <td className="py-2 pr-2"></td>
                    <td className="py-2 pr-2 text-[10px] font-mono font-bold tracking-widest text-amber-300">ALT</td>
                    <td className="py-2 pr-2 font-display font-bold uppercase text-amber-300 tracking-widest text-xs">
                      Alternates Subtotal (not in grand total)
                    </td>
                    <td colSpan={4}></td>
                    <td className="py-2 pr-2 text-right font-display font-bold text-amber-300 text-sm" data-testid="sov-alternates-subtotal">
                      ${sovAlternatesTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
